import {
  Alert,
  Badge,
  Box,
  Card,
  Group,
  Loader,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import dayjs from "dayjs";
import { useMemo } from "react";
import { Line } from "react-chartjs-2";
import { AlertCircle, ChartLine } from "tabler-icons-react";

const ClientConversionChart = ({
  prospectsListData,
  isLoading,
  error,
  dateRange,
  periodFilter,
  provenanceFilter,
  chartOptions,
}) => {
  const conversionTrendData = useMemo(() => {
    const labels = [];
    const dataMap = new Map();
    const { start, end } = dateRange;
    const isYearOrAll = periodFilter === "year" || periodFilter === "all";
    const grain = isYearOrAll ? "month" : "day";
    const normalize = (value) =>
      String(value ?? "")
        .trim()
        .toLowerCase();

    if (isYearOrAll) {
      let currMonth = start.clone().startOf("month");
      while (currMonth.isBefore(end) || currMonth.isSame(end, "month")) {
        const label = currMonth.format("MMM YYYY");
        labels.push(label);
        dataMap.set(label, 0);
        currMonth = currMonth.add(1, "month");
      }
    } else {
      let current = start.clone();
      while (current.isBefore(end) || current.isSame(end, "day")) {
        const label = current.format("DD MMM");
        labels.push(label);
        dataMap.set(label, 0);
        current = current.add(1, "day");
      }
    }

    const prospects = Array.isArray(prospectsListData?.data)
      ? prospectsListData.data
      : Array.isArray(prospectsListData)
        ? prospectsListData
        : [];

    prospects.forEach((parent) => {
      if (provenanceFilter !== "all") {
        if (normalize(parent.provenance) !== normalize(provenanceFilter)) {
          return;
        }
      }

      const conversionDate = parent.date_conversion || parent.dateConversion;
      if (!conversionDate) return;

      const convertedAt = dayjs(conversionDate);
      if (!convertedAt.isValid()) return;
      if (!convertedAt.isBetween(start, end, grain, "[]")) return;

      const bucketLabel = isYearOrAll
        ? convertedAt.format("MMM YYYY")
        : convertedAt.format("DD MMM");

      if (dataMap.has(bucketLabel)) {
        dataMap.set(bucketLabel, dataMap.get(bucketLabel) + 1);
      }
    });

    const values = labels.map((label) => dataMap.get(label));
    const total = values.reduce((sum, value) => sum + value, 0);

    const peak = values.reduce(
      (acc, value, index) => {
        if (value > acc.value) {
          return { value, label: labels[index] };
        }
        return acc;
      },
      { value: 0, label: "-" },
    );

    return {
      labels,
      values,
      total,
      average: labels.length ? (total / labels.length).toFixed(1) : "0.0",
      peak,
    };
  }, [prospectsListData, dateRange, periodFilter, provenanceFilter]);

  const conversionChartData = {
    labels: conversionTrendData.labels,
    datasets: [
      {
        fill: true,
        label: "Conversions clients",
        data: conversionTrendData.values,
        borderColor: "#16a34a",
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 400);
          gradient.addColorStop(0, "rgba(22, 163, 74, 0.45)");
          gradient.addColorStop(1, "rgba(22, 163, 74, 0.0)");
          return gradient;
        },
        tension: 0.35,
        pointRadius: 4,
        pointBackgroundColor: "#ffffff",
        pointBorderColor: "#16a34a",
        borderWidth: 2,
      },
    ],
  };

  return (
    <Box>
      <Group spacing="xs" mb="md">
        <ThemeIcon variant="light" color="green" size="md">
          <ChartLine size={18} />
        </ThemeIcon>
        <Title order={3}>Conversions en clients</Title>
      </Group>
      <Text size="sm" color="dimmed" mb="lg">
        Suivi des prospects devenus clients base sur la date de conversion.
      </Text>

      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            padding: "40px 0",
          }}
        >
          <Loader size="lg" />
        </Box>
      ) : error ? (
        <Alert
          icon={<AlertCircle size={16} />}
          title="Erreur"
          color="red"
          mb="md"
        >
          Impossible de charger les dates de conversion clients.
        </Alert>
      ) : (
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group position="apart" mb="md" wrap="wrap">
            <Group spacing="sm">
              <Badge color="green" variant="light" size="lg">
                {conversionTrendData.total.toLocaleString("fr-FR")} conversions
              </Badge>
              <Badge color="teal" variant="light" size="lg">
                Moyenne: {conversionTrendData.average}
              </Badge>
            </Group>
            <Text size="sm" color="dimmed">
              Pic: {conversionTrendData.peak.value} (
              {conversionTrendData.peak.label})
            </Text>
          </Group>

          <div style={{ height: "320px" }}>
            <Line options={chartOptions} data={conversionChartData} />
          </div>
        </Card>
      )}
    </Box>
  );
};

export default ClientConversionChart;
